import {
  deserializeBorsh,
  GOVERNANCE_SCHEMA,
  ProgramAccount,
  Proposal,
  ProposalState,
} from "@solana/spl-governance";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import {
  ComputeBudgetProgram,
  Connection,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  TransactionInstruction,
} from "@solana/web3.js";
import { getClubs, getDashboardClubs } from "../../../api/clubs.api";
import {
  IBasicClubData,
  IDepositRecord,
  IReservedRights,
  ITreasuryTokenAccInfo,
  RoleConfig,
} from "../../../common/interfaces/clubs.interface";
import { divide, multiply, pow } from "mathjs";
import {
  escrowProgramFactory,
  financialRecordSeed,
  governanceSeed,
  mint,
  profitSeed,
  programFactory,
  RPC_CONNECTION,
  splGovernanceProgram,
  stakeConfigSeed,
  stakeRecordSeed,
  treasuryDataSeed,
  treasurySeed,
  udmProgramFactory,
  unqClubMemberSeed,
  unqClubSeed,
  vaultSeed,
  withdrawalRecordSeed,
  withdrawalSeed,
} from "../utilities";
import {
  IDashboardBasicClubData,
  IDashboardApplication,
  IDashboardStakingInfo,
  IDashboardTokenAccountInfo,
  IDashboardTreasury,
  IDenominatedTokenInfo,
  IDepositData,
  IMembersClubData,
  INftMemberAmountPerClub,
  IProposalData,
  IWithdrawalDataDashboard,
} from "../../../common/interfaces/user-dashboard.interface";
import {
  ACCOUNT_RENT,
  CHAIN_ID,
  NATIVE_MINT_DECIMAL_PLACES,
  TREASURY_INDEX,
  UNQ_PER_AMOUNT,
} from "../../../common/constants/common.constants";
import { chunk } from "lodash";
import {
  getAccumulatedAmountForDepositRecord,
  getInfoForMintAccounts,
  getMemberReservedRights,
  getMultipleDenominatedTokensInfo,
  getTreasuyTokenAccountsInfo,
  mapClubActionFromSolanaToClubAction,
  parseReserverRightsFromProgramToTreasuryInfo,
} from "./common.helpers";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  Token,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { ClubType } from "../../../common/enums/club.enum";
import {
  getDecimalsPerMint,
  getMintDecimalsDivider,
  getTreasuryCount,
  round,
} from "../../../utilities/helpers";
import {
  ClubActionSolana,
  TradedRightType,
  UserRole,
} from "../../../common/enums/common.enums";
import dayjs from "dayjs";
import { BN } from "@project-serum/anchor";
import { getAllApplications } from "../../../api/applications.api";
import { claimWithdrawalAmount } from "../../methods/proposals";
import {
  Rarity,
  StakePeriodTypeSolana,
  StakingStatusSolana,
} from "../../../common/enums/staking.enum";
import { IMemberStakingInfo } from "../../../common/interfaces/staking.interface";
import {
  getAdditionalReward,
  getFixedRewardPercentage,
  getStakingPeriodType,
  getStatusForStakingAccount,
  mapStakingStatusFromSolana,
} from "./staking.helpers";
import { getUserApplications } from "../../../api/users.api";
import { sendTransactions } from "../../transactions/sendTransactions";
import {
  IDelegation,
  IInstructionSet,
} from "../../../common/interfaces/common.interface";
import { getMintMetadata } from "../../methods/general";
import { IBasicProposalData } from "../../../common/interfaces/proposals.interface";
import {
  ProposalStatus,
  ProposalTypeProgramString,
} from "../../../common/enums/proposals.enums";
import {
  mapProposalAction,
  mapProposalTypeFromProgram,
} from "./proposals.helpers";
import {
  getFinancialOfferPDAs,
  getFinancialRecordPDA,
} from "../../methods/treasury";
import { mapDepositRecords } from "../../methods/clubs";

export const getMastersForPublicKey = async (
  publicKey: PublicKey
): Promise<IDelegation[]> => {
  try {
    const representations: IDelegation[] = [];
    const program = udmProgramFactory();
    const allDelegationsAccounts = await program.account.delegation.all([
      {
        memcmp: {
          offset: 8 + 32,
          bytes: publicKey.toBase58(),
        },
      },
    ]);
    allDelegationsAccounts.forEach((item) => {
      item.account.authorised &&
        representations.push({
          authorised: item.account.authorised,
          master: item.account.master,
          representative: item.account.representative,
        });
    });

    return representations;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export async function getRealmsForUser(wallet: AnchorWallet) {
  try {
    const clubProgram = programFactory();
    const allClubs: IBasicClubData[] = await getDashboardClubs();
    const clubsFromSolana = await clubProgram.account.clubData.all();
    const masterDelegations = await getMastersForPublicKey(wallet.publicKey);
    const usersRealms: IMembersClubData[] = [];
    const memberPdas: PublicKey[] = [];
    const clubFromSolanaData: {
      realm: PublicKey;
      mint: PublicKey;
      type: ClubType;
      roleConfig: any;
    }[] = [];

    for (const solanaClub of clubsFromSolana) {
      const club: IBasicClubData = allClubs[solanaClub.publicKey.toString()];
      if (club) {
        clubFromSolanaData.push({
          mint: club.communityMintAddress
            ? new PublicKey(club.communityMintAddress)
            : mint,
          realm: solanaClub.account.realm,
          type: solanaClub.account.clubType,
          roleConfig: solanaClub.account.roleConfig,
        });
        const [memberPDA] = await PublicKey.findProgramAddress(
          [
            unqClubSeed,
            solanaClub.publicKey.toBuffer(),
            unqClubMemberSeed,
            wallet.publicKey.toBuffer(),
          ],
          clubProgram.programId
        );

        memberPdas.push(memberPDA);

        //Master wallets
        for (const master of masterDelegations) {
          const [masterMemberPDA] = await PublicKey.findProgramAddress(
            [
              unqClubSeed,
              solanaClub.publicKey.toBuffer(),
              unqClubMemberSeed,
              master.master.toBuffer(),
            ],
            clubProgram.programId
          );
          memberPdas.push(masterMemberPDA);
        }
      }
    }

    const memberDataAccounts: any[] =
      await clubProgram.account.allowedMemberData.fetchMultiple(memberPdas);

    for (const memberAccount of memberDataAccounts) {
      if (!memberAccount) continue;

      const club = clubsFromSolana.find(
        (c) => c.publicKey.toString() === memberAccount.clubData.toString()
      );
      if (!club) continue;

      const clubFromApi = allClubs[memberAccount.clubData.toString()];
      const relatedClubFromSolana = clubFromSolanaData.find(
        (club) => club.realm.toString() === clubFromApi.realmAddress
      );

      if (!clubFromApi || !relatedClubFromSolana) continue;
      const roleConfigRaw: any = relatedClubFromSolana.roleConfig;
      const roleConfig: RoleConfig[] = roleConfigRaw.map((item) => {
        return {
          name: item.name,
          roleWeight: item.roleWeight.toNumber(),
          actions: item.clubActions.map((item) =>
            mapClubActionFromSolanaToClubAction(
              Object.keys(item).toString() as ClubActionSolana
            )
          ),
        };
      });

      usersRealms.push({
        invitationStatus: memberAccount.status as number,
        role: memberAccount.role,
        club: {
          clubAddress: clubFromApi.address.toString(),
          clubImageUrl: clubFromApi.imageUrl,
          clubName: clubFromApi.name,
          realmAddress: clubFromApi.realmAddress,
          type: relatedClubFromSolana.type,
          communityMint: clubFromApi.communityMintAddress
            ? new PublicKey(clubFromApi.communityMintAddress)
            : mint,
          numberOfMembers: club.account.numberOfMembers,
          stakeCount: clubsFromSolana.find(
            (item) =>
              item.publicKey.toString() === memberAccount.clubData.toString()
          )?.account.stakeCount!,
          roleConfig: roleConfig,
        },
        clubAuthority: clubFromApi.owner!,
        masterAddres:
          memberAccount.memberPubkey.toString() !== wallet.publicKey.toString()
            ? memberAccount.memberPubkey
            : undefined,
      });
    }

    return usersRealms;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function getMasterClubsForUser(wallet: AnchorWallet) {
  try {
    const clubProgram = programFactory();
    const allClubs: IBasicClubData[] = await getClubs();
    const clubsFromSolana = await clubProgram.account.clubData.all();
    const masterDelegations = await getMastersForPublicKey(wallet.publicKey);
    if (masterDelegations.length === 0) {
      return [];
    }
    const usersRealms: IMembersClubData[] = [];
    const memberPdas: PublicKey[] = [];
    const clubFromSolanaData: {
      realm: PublicKey;
      mint: PublicKey;
      type: ClubType;
      roleConfig: any;
    }[] = [];

    for (const solanaClub of clubsFromSolana) {
      const club: IBasicClubData = allClubs[solanaClub.publicKey.toString()];
      if (club) {
        clubFromSolanaData.push({
          mint: club.communityMintAddress
            ? new PublicKey(club.communityMintAddress)
            : mint,
          realm: solanaClub.account.realm,
          type: solanaClub.account.clubType,
          roleConfig: solanaClub.account.roleConfig,
        });

        //Master wallets
        for (const master of masterDelegations) {
          const [masterMemberPDA] = await PublicKey.findProgramAddress(
            [
              unqClubSeed,
              solanaClub.publicKey.toBuffer(),
              unqClubMemberSeed,
              master.master.toBuffer(),
            ],
            clubProgram.programId
          );
          memberPdas.push(masterMemberPDA);
        }
      }
    }

    const memberDataAccounts: any[] =
      await clubProgram.account.allowedMemberData.fetchMultiple(memberPdas);

    for (const memberAccount of memberDataAccounts) {
      if (!memberAccount) continue;

      const club = clubsFromSolana.find(
        (c) => c.publicKey.toString() === memberAccount.clubData.toString()
      );
      if (!club) continue;

      const clubFromApi = allClubs[memberAccount.clubData.toString()];
      const relatedClubFromSolana = clubFromSolanaData.find(
        (club) => club.realm.toString() === clubFromApi.realmAddress
      );

      if (!clubFromApi || !relatedClubFromSolana) continue;
      const roleConfigRaw: any = relatedClubFromSolana.roleConfig;
      const roleConfig: RoleConfig[] = roleConfigRaw.map((item) => {
        return {
          name: item.name,
          roleWeight: item.roleWeight.toNumber(),
          actions: item.clubActions.map((item) =>
            mapClubActionFromSolanaToClubAction(
              Object.keys(item).toString() as ClubActionSolana
            )
          ),
        };
      });

      usersRealms.push({
        invitationStatus: memberAccount.status as number,
        role: memberAccount.role,
        club: {
          clubAddress: clubFromApi.address.toString(),
          clubImageUrl: clubFromApi.imageUrl,
          clubName: clubFromApi.name,
          realmAddress: clubFromApi.realmAddress,
          type: relatedClubFromSolana.type,
          communityMint: clubFromApi.communityMintAddress
            ? new PublicKey(clubFromApi.communityMintAddress)
            : mint,
          numberOfMembers: club.account.numberOfMembers,
          stakeCount: clubsFromSolana.find(
            (item) =>
              item.publicKey.toString() === memberAccount.clubData.toString()
          )?.account.stakeCount!,
          roleConfig: roleConfig,
        },
        clubAuthority: clubFromApi.owner!,
        masterAddres: memberAccount.memberPubkey,
      });
    }

    return usersRealms;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function getAmountOfProposalsForRealm(
  clubData: IMembersClubData[]
) {
  try {
    const proposalDatas: IBasicProposalData[] = [];
    const clubProgram = programFactory();
    let proposalMetadata = await clubProgram.account.proposalMetadata.all();
    let filteredMetadatas: any[] = [];
    for (const club of clubData) {
      proposalMetadata.forEach((metadata) => {
        if (
          metadata.account.realm.toString() ===
          club.club.realmAddress.toString()
        ) {
          filteredMetadatas.push(metadata);
        }
      });
    }
    let proposalPubkeys = filteredMetadatas.map(
      (metadata) => metadata.account.proposal
    );
    proposalPubkeys = chunk(proposalPubkeys, 100);
    for (const [
      arrayIndex,
      proposalPubkeysArray,
    ] of proposalPubkeys.entries()) {
      const rawData = await RPC_CONNECTION.getMultipleAccountsInfoAndContext(
        proposalPubkeysArray
      );
      for (const [index, rawAccount] of rawData.value.entries()) {
        if (!rawAccount) continue;
        const deserializedAccount = deserializeBorsh(
          GOVERNANCE_SCHEMA,
          Proposal,
          Buffer.from(rawAccount!.data)
        );
        const metadataAccount = filteredMetadatas[index].account;
        const proposalType = mapProposalTypeFromProgram(
          Object.keys(
            metadataAccount.proposalType
          )[0] as ProposalTypeProgramString
        );
        proposalDatas.push({
          proposalAccount: {
            account: deserializedAccount,
            owner: splGovernanceProgram,
            pubkey: proposalPubkeys[arrayIndex][index],
          },
          proposalMetadata: {
            currentFundraiseIndex: metadataAccount.currentFundraiseIndex,
            description: metadataAccount.description,
            discussionLink: metadataAccount.discussionLink,
            governance: metadataAccount.governance,
            voteThreshold: metadataAccount.approvalQuorumPercentage,
            totalFinanacialPower:
              metadataAccount.totalFinancialPower.toNumber(),
            treasury: metadataAccount.treasury,
            proposalMetadata: filteredMetadatas[index].publicKey,
            proposalIndex: metadataAccount.proposalIndex,
            maxVoterWeight: metadataAccount.maxVoterWeight
              ? metadataAccount.maxVoterWeight.toNumber()
              : undefined,
            proposalStatus: Object.keys(
              metadataAccount.proposalStatus
            )[0] as ProposalStatus,
            proposalType,
            proposalAction: mapProposalAction(proposalType),
            createdAt: metadataAccount.createdAt.toNumber(),
          },
        });
      }
    }
    const filteredProposals: IProposalData[] = [];
    proposalDatas.forEach((p) => {
      if (
        p.proposalAccount.account.state === ProposalState.Voting ||
        (p.proposalAccount.account.state === ProposalState.Succeeded &&
          p.proposalAccount.account.options[0].instructionsCount >= 1) ||
        p.proposalAccount.account.state === ProposalState.Executing
      )
        filteredProposals.push({
          proposalData: p,
          realmAddress: filteredMetadatas.find(
            (metadata) =>
              metadata.account.proposal.toString() ===
              p.proposalAccount.pubkey.toString()
          ).account.realm,
        });
    });
    const sortedProposals = filteredProposals.sort((p1, p2) =>
      p1.proposalData.proposalAccount.account.draftAt <
      p2.proposalData.proposalAccount.account.draftAt
        ? 1
        : -1
    );
    return sortedProposals;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const getWithdrawalsData = async (
  realmDatas: IMembersClubData[],
  treasuryDatas: (IDashboardTreasury | null)[],
  wallet: AnchorWallet,
  deposits: IDepositData[]
) => {
  try {
    const treasuryBuffer = Buffer.alloc(4);
    treasuryBuffer.writeInt32LE(TREASURY_INDEX + 1, 0);
    const clubProgram = programFactory();
    const escrowProgram = escrowProgramFactory();
    const allWithdrawalDatas = await escrowProgram.account.withdrawalData.all();
    const withdrawalDatas = allWithdrawalDatas.filter((withdrawalData) =>
      realmDatas.find(
        (realm) =>
          realm.club.realmAddress === withdrawalData.account.realm.toString()
      )
    );
    const allWithdrawalMintsInfo = await getInfoForMintAccounts(
      withdrawalDatas.map((item) => item.account.withdrawalMint)
    );
    const withdrawalRecordsInfo: {
      withdrawal: PublicKey;
      withdrawalRecord: PublicKey;
    }[] = [];
    const dashboardWithdrawalsInfo: IWithdrawalDataDashboard[] = [];
    for (const withdrawalData of withdrawalDatas) {
      const [withdrawalAddress] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          withdrawalData.account.proposal.toBuffer(),
          withdrawalData.account.treasury.toBuffer(),
          withdrawalSeed,
        ],
        escrowProgram.programId
      );

      const [withdrawalDataRecordAddress] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          withdrawalAddress.toBuffer(),
          withdrawalRecordSeed,
          wallet.publicKey.toBuffer(),
        ],
        escrowProgram.programId
      );

      const [treasury] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          withdrawalData.account.clubData.toBuffer(),
          treasurySeed,
          treasuryBuffer,
        ],
        clubProgram.programId
      );

      const [withdrawal] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          withdrawalData.account.proposal.toBuffer(),
          treasury.toBuffer(),
          withdrawalSeed,
        ],
        escrowProgram.programId
      );

      withdrawalRecordsInfo.push({
        withdrawal,
        withdrawalRecord: withdrawalDataRecordAddress,
      });

      const relatedTreasuryData = treasuryDatas.find(
        (item) => item?.treasuryAddress.toString() === treasury.toString()
      );

      let hasWithdrawn: boolean = false;
      let withdrawnAmount: number = 0;
      const clubData = realmDatas.find(
        (data) =>
          data.club.realmAddress === withdrawalData.account.realm.toString()
      );
      const withdrawalMintInfo = allWithdrawalMintsInfo.find(
        (item) =>
          item.address.toString() ===
          withdrawalData.account.withdrawalMint.toString()
      );
      dashboardWithdrawalsInfo.push({
        totalFinancialPower: divide(
          withdrawalData.account.totalFinancialPower.toNumber(),
          getDecimalsPerMint(relatedTreasuryData?.denominatedTokenInfo)
        ),
        totalWithdraw: round(
          withdrawalData.account.withdrawalAmount
            .div(
              new BN(
                withdrawalMintInfo
                  ? getMintDecimalsDivider(withdrawalMintInfo.decimals)
                  : LAMPORTS_PER_SOL
              )
            )
            .toNumber()
        ),
        fundraiseCount: withdrawalData.account.fundraiseNumber,
        club: clubData!.club,
        relatedProposal: withdrawalData.account.proposal,
        withdrawalDataAddress: withdrawalData.publicKey,
        hasWithdrawn,
        withdrawalRecordAmount: withdrawnAmount,
        withdrawal,
        withdrawalMint: withdrawalData.account.withdrawalMint,
        withdrawalMintDecimals: withdrawalMintInfo?.decimals,
        withdrawalMintSymbol: withdrawalMintInfo?.symbol,
        treasury: withdrawalData.account.treasury,
        proposalCreatedAt: withdrawalData.account.proposalCreatedAt.toNumber(),
      });
    }
    const withdrawalRecords = withdrawalRecordsInfo.map(
      (record) => record.withdrawalRecord
    );
    const withdrawalRecordAccounts =
      await escrowProgram.account.withdrawalRecord.fetchMultiple(
        withdrawalRecords
      );
    for (const [
      index,
      withdrawalRecord,
    ] of withdrawalRecordAccounts.entries()) {
      const relatedWithdrawalInfoIndex = dashboardWithdrawalsInfo.findIndex(
        (info) =>
          info.withdrawal.toString() ===
          withdrawalRecordsInfo[index].withdrawal.toString()
      );
      if (withdrawalRecord) {
        const witdrawalRecordData = withdrawalRecord as any;
        const updatedWitdrawalInfo = {
          ...dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex],
          hasWithdrawn: true,
          withdrawalRecordAmount: witdrawalRecordData.withdrawnAmount
            .div(
              new BN(
                dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex]
                  .withdrawalMintDecimals
                  ? Number(
                      pow(
                        10,
                        dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex]
                          .withdrawalMintDecimals!
                      )
                    )
                  : LAMPORTS_PER_SOL
              )
            )
            .toNumber(),
        };
        dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex] =
          updatedWitdrawalInfo;
      } else {
        let depositAmount = 0;
        const clubDepositData = deposits.find(
          (item) =>
            item.treasury.toString() ===
            dashboardWithdrawalsInfo[
              relatedWithdrawalInfoIndex
            ].treasury.toString()
        );
        const denomimatedCurrency = treasuryDatas.find(
          (item) =>
            item?.treasuryAddress.toString() ===
            dashboardWithdrawalsInfo[
              relatedWithdrawalInfoIndex
            ].treasury.toString()
        )?.denominatedTokenInfo;

        if (clubDepositData) {
          const accumulatedAmount = getAccumulatedAmountForDepositRecord(
            clubDepositData?.depositRecords,
            dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex].fundraiseCount,
            dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex]
              .proposalCreatedAt
          );
          depositAmount =
            accumulatedAmount / getDecimalsPerMint(denomimatedCurrency);
        }

        const relatedTreasuryData = treasuryDatas.find(
          (item) =>
            item?.treasuryAddress.toString() ===
            dashboardWithdrawalsInfo[
              relatedWithdrawalInfoIndex
            ].treasury.toString()
        );

        let withdrawalAmount = multiply(
          divide(
            depositAmount,
            dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex]
              .totalFinancialPower!
          ),
          dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex].totalWithdraw
        );

        const memberReservedRights = getMemberReservedRights(
          wallet.publicKey,
          relatedTreasuryData?.reservedRights
        );

        if (memberReservedRights !== undefined) {
          withdrawalAmount =
            withdrawalAmount +
            multiply(
              memberReservedRights.amountOfRights /
                dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex]
                  .totalFinancialPower!,
              dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex].totalWithdraw
            );
        }

        const updatedWitdrawalInfo = {
          ...dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex],
          hasWithdrawn: false,
        };
        updatedWitdrawalInfo.withdrawalRecordAmount = round(
          withdrawalAmount,
          dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex]
            .withdrawalMintDecimals
        );
        dashboardWithdrawalsInfo[relatedWithdrawalInfoIndex] =
          updatedWitdrawalInfo;
      }
    }
    return dashboardWithdrawalsInfo;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const calculateWithdrawnAmount = (
  withdrawals: IWithdrawalDataDashboard[],
  mint: PublicKey
) => {
  try {
    const totalWithdrawnAmount = withdrawals
      .filter((item) => item.withdrawalMint.toString() === mint.toString())
      .reduce((prev, next) => next && prev + next.withdrawalRecordAmount, 0);
    return round(totalWithdrawnAmount, 9);
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const calculateAmountToBeWithdrawn = (
  withdrawals: IWithdrawalDataDashboard[],
  denomimatedCurrency: ITreasuryTokenAccInfo | undefined,
  deposits: IDepositData[],
  reservedRights: IReservedRights | undefined,
  memberPubkey: PublicKey
) => {
  try {
    let totalWithdrawableAmount = 0;
    for (const withdrawal of withdrawals) {
      let depositAmount = 0;
      const clubsDeposit = deposits.find(
        (item) => item.treasury.toString() === withdrawal.treasury.toString()
      );
      if (clubsDeposit) {
        const accumulatedAmount = getAccumulatedAmountForDepositRecord(
          clubsDeposit?.depositRecords,
          withdrawal.fundraiseCount,
          withdrawal.proposalCreatedAt
        );
        depositAmount =
          accumulatedAmount / getDecimalsPerMint(denomimatedCurrency);

        totalWithdrawableAmount += multiply(
          divide(depositAmount, withdrawal.totalFinancialPower!),
          withdrawal.totalWithdraw
        );
      }

      const memberReservedRights = getMemberReservedRights(
        memberPubkey,
        reservedRights
      );
      if (memberReservedRights) {
        totalWithdrawableAmount += multiply(
          divide(
            memberReservedRights.amountOfRights,
            withdrawal.totalFinancialPower!
          ),
          withdrawal.totalWithdraw
        );
      }
    }
    return round(totalWithdrawableAmount, denomimatedCurrency?.decimals);
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getTreasuryDataForClubs = async (
  clubs: IMembersClubData[],
  wallet: PublicKey
) => {
  try {
    const treasuryDataPdas: PublicKey[] = [];
    const treasuryPdas: PublicKey[] = [];
    const profitPdas: PublicKey[] = [];
    const financialRecordPdas: PublicKey[] = [];
    const allTreasuriesAmounts: any[] = [];
    const allProfitAmounts: any[] = [];
    const ownershipData: (IDashboardTreasury | null)[] = [];

    const rent = await RPC_CONNECTION.getMinimumBalanceForRentExemption(0);
    const clubProgram = programFactory();

    for (const club of clubs) {
      const [treasury] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          new PublicKey(club.club.clubAddress).toBuffer(),
          treasurySeed,
          getTreasuryCount(TREASURY_INDEX),
        ],
        clubProgram.programId
      );
      const [profit] = await PublicKey.findProgramAddress(
        [unqClubSeed, treasury.toBuffer(), profitSeed],
        clubProgram.programId
      );

      const [treasuryData] = await PublicKey.findProgramAddress(
        [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
        clubProgram.programId
      );
      treasuryDataPdas.push(treasuryData);
      treasuryPdas.push(treasury);
      profitPdas.push(profit);
    }
    const treasuryDataAccounts: any[] =
      await clubProgram.account.treasuryData.fetchMultiple(treasuryDataPdas);

    const chunkedTreasuryPdas = chunk(treasuryPdas, 100);
    for (const chunkedTreasury of chunkedTreasuryPdas) {
      const treasuryAmounts = await RPC_CONNECTION.getMultipleAccountsInfo(
        chunkedTreasury
      );
      treasuryAmounts.forEach((item) => {
        allTreasuriesAmounts.push(item);
      });
    }

    const chunkedProfitPdas = chunk(profitPdas, 100);
    for (const chunkedProfit of chunkedProfitPdas) {
      const profitAmounts = await RPC_CONNECTION.getMultipleAccountsInfo(
        chunkedProfit
      );
      profitAmounts.forEach((item) => {
        allProfitAmounts.push(item);
      });
    }
    for (const [index, treasuryDataAcc] of treasuryDataAccounts.entries()) {
      if (!treasuryDataAcc) continue;
      for (let i = treasuryDataAcc.fundraiseCount; i > 0; i--) {
        try {
          const fundraiseCountBuff = Buffer.alloc(4);
          fundraiseCountBuff.writeInt32LE(i, 0);
          const [financialRecordAddress] = await PublicKey.findProgramAddress(
            [
              unqClubSeed,
              treasuryDataPdas[index].toBuffer(),
              financialRecordSeed,
              wallet.toBuffer(),
            ],
            clubProgram.programId
          );
          financialRecordPdas.push(financialRecordAddress);
        } catch (error) {
          console.log(error);
        }
      }
    }

    //Add chunkes
    const allfinancialRecordsForMemberRaw: any = (
      await clubProgram.account.financialRecord.fetchMultiple(
        financialRecordPdas
      )
    ).filter((item) => item !== null);

    const allDepositInfosForMember: IDepositData[] = [];
    for (const rawDepositRecord of allfinancialRecordsForMemberRaw) {
      const { accumulatedAmount, boughtAmount, soldAmount, deposits } =
        getOwnershipInClub(rawDepositRecord);

      allDepositInfosForMember.push({
        treasury: rawDepositRecord.treasury,
        depositAmount: accumulatedAmount,
        depositRecords: deposits,
        boughtAmount: boughtAmount,
        soldAmount: soldAmount,
      });
    }
    const allDenominatedCurrencies: IDenominatedTokenInfo[] =
      treasuryDataAccounts
        .filter((item) => item !== undefined && item !== null)
        .map((item) => {
          return {
            denominatedCurrency: item.denominatedCurrency,
            financialTokenAccount: item.financialTokenAccount,
            treasuryAddress: item.treasury,
          };
        });

    const allDenominatedTokensInfo = await getMultipleDenominatedTokensInfo(
      allDenominatedCurrencies.filter(
        (item) => item.financialTokenAccount && item.denominatedCurrency
      )
    );

    for (const [index, treasuryData] of treasuryDataAccounts.entries()) {
      const [treasury] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          new PublicKey(clubs[index].club.clubAddress).toBuffer(),
          treasurySeed,
          getTreasuryCount(TREASURY_INDEX),
        ],
        clubProgram.programId
      );
      const treasuryAmount =
        (Number(allTreasuriesAmounts[index]?.lamports) - rent) /
        LAMPORTS_PER_SOL;

      const denominatedTokenInfo =
        treasuryData &&
        treasuryData.denominatedCurrency !== null &&
        treasuryData.financialTokenAccount !== null
          ? allDenominatedTokensInfo.find(
              (item) =>
                item.treasury.toString() === treasuryData.treasury.toString()
            )?.tokenInfo
          : undefined;
      if (denominatedTokenInfo) {
        const [profit] = await PublicKey.findProgramAddress(
          [unqClubSeed, treasury.toBuffer(), profitSeed],
          clubProgram.programId
        );
        const profitAccount =
          await RPC_CONNECTION.getParsedTokenAccountsByOwner(profit, {
            mint: denominatedTokenInfo.mint,
          });
        denominatedTokenInfo.balance =
          denominatedTokenInfo.balance +
          (profitAccount.value[0].account.data.parsed.info.tokenAmount
            .uiAmount ?? 0);
        denominatedTokenInfo.profit =
          profitAccount.value[0].account.data.parsed.info.tokenAmount
            .uiAmount ?? 0;
      }
      if (treasuryData === null) {
        ownershipData.push(null);
        continue;
      }
      const memberDepositInfoForClub = allDepositInfosForMember.find(
        (d) => d.treasury.toString() === treasuryData.treasury.toString()
      );

      let membersFinancialPower = 0;

      const decimalsForAmount = getDecimalsPerMint(denominatedTokenInfo);
      let calculatedOwnership = 0;
      let totalDeposit = 0;

      if (memberDepositInfoForClub) {
        membersFinancialPower += divide(
          memberDepositInfoForClub.depositAmount +
            memberDepositInfoForClub.boughtAmount -
            memberDepositInfoForClub.soldAmount,
          decimalsForAmount
        );
        totalDeposit += memberDepositInfoForClub.depositAmount;
        calculatedOwnership += multiply(
          divide(
            membersFinancialPower ?? 0,
            divide(
              treasuryData.totalFinancialPower.toNumber(),
              decimalsForAmount
            )
          ),
          100
        ) as number;
      }

      if (treasuryData.reservedRights) {
        const memberReservedRights =
          treasuryData.reservedRights.individualRights.find(
            (item) => item.memberPubkey.toString() === wallet.toString()
          );
        if (memberReservedRights) {
          membersFinancialPower +=
            memberReservedRights.amountOfRights.toNumber();

          if (
            memberReservedRights.amountOfRights.toNumber() > 0 &&
            treasuryData.totalFinancialPower.toNumber() > 0
          ) {
            calculatedOwnership =
              calculatedOwnership +
              divide(
                memberReservedRights.amountOfRights.toNumber() * 100,
                treasuryData.totalFinancialPower.toNumber()
              );
          }
        }
      }

      let profitAmount =
        (Number(allProfitAmounts[index]?.lamports) - ACCOUNT_RENT) /
        LAMPORTS_PER_SOL;

      if (treasuryData.denominatedCurrency) {
        profitAmount += treasuryAmount;
      }

      ownershipData.push({
        ownership: round(calculatedOwnership, 2) ?? 0,
        financialPower: membersFinancialPower,
        balance: denominatedTokenInfo
          ? denominatedTokenInfo.balance
          : treasuryAmount + profitAmount,
        currency: CHAIN_ID.toUpperCase(),
        deposits: totalDeposit / decimalsForAmount,
        isFundraisingInProgress: treasuryData.hasActiveFundraise,
        club: clubs[index].club,
        treasuryAddress: treasury,
        profit: denominatedTokenInfo
          ? denominatedTokenInfo.profit
          : profitAmount,
        fundraiseCount: treasuryData.fundraiseCount,
        withdrawalGovernance: treasuryData.withdrawalGovernance,
        denominatedTokenInfo: denominatedTokenInfo,
        treasuryLamports: treasuryAmount,
        reservedRights: treasuryData.reservedRights
          ? parseReserverRightsFromProgramToTreasuryInfo(
              treasuryData.reservedRights,
              treasuryData.reservedRights?.individualRights,
              getDecimalsPerMint(denominatedTokenInfo)
            )
          : undefined,
      });
    }

    return {
      treasuryInfo: ownershipData,
      deposits: allDepositInfosForMember,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getNumberOfNftsForRealm = async (clubs: IMembersClubData[]) => {
  try {
    const clubProgram = programFactory();
    const nfts: INftMemberAmountPerClub[] = [];
    for (const club of clubs) {
      const [treasury] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          new PublicKey(club.club.clubAddress).toBuffer(),
          treasurySeed,
          getTreasuryCount(TREASURY_INDEX),
        ],
        clubProgram.programId
      );

      const [vaultAddress] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          new PublicKey(club.club.clubAddress).toBuffer(),
          treasury.toBuffer(),
          vaultSeed,
          Buffer.from(CHAIN_ID),
        ],
        clubProgram.programId
      );
      const vaultTokenAccounts =
        await RPC_CONNECTION.getParsedTokenAccountsByOwner(vaultAddress, {
          programId: TOKEN_PROGRAM_ID,
        });
      const vaultNfts = vaultTokenAccounts.value.filter(
        (tokenAcc) =>
          tokenAcc.account.data.parsed.info.tokenAmount.decimals === 0 &&
          tokenAcc.account.data.parsed.info.tokenAmount.amount >= 1
      );
      nfts.push({
        clubAddress: club.club.clubAddress,
        nftMemberAmount: vaultNfts.length,
      });
    }
    return nfts;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getWithdrawalProposalsInfo = async (proposals: PublicKey[]) => {
  try {
    const splProposals: ProgramAccount<Proposal>[] = [];
    const chunkedProposalArray = chunk(proposals, 100);
    for (const chunkedProposals of chunkedProposalArray) {
      const rawProposals =
        await RPC_CONNECTION.getMultipleAccountsInfoAndContext(
          chunkedProposals
        );
      for (const [index, proposal] of rawProposals.value.entries()) {
        if (!proposal) {
          proposals.splice(index, 1);
          continue;
        }
        const deserializedProposal = deserializeBorsh(
          GOVERNANCE_SCHEMA,
          Proposal,
          proposal.data
        );
        splProposals.push({
          account: deserializedProposal,
          owner: splGovernanceProgram,
          pubkey: proposals[index],
        });
      }
    }
    return splProposals;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const parseUnixToDate = (timestamp: BN) => {
  const date = dayjs.unix(timestamp.toNumber());
  return dayjs(date).format("DD/MM/YY HH:mm");
};

export const getApplications = async (clubs: IMembersClubData[]) => {
  try {
    const myClubs = clubs.filter((club) => club.role === UserRole.owner);
    const applicationData: IDashboardApplication[] = [];
    for (const club of myClubs) {
      const applicationsForClub = await getAllApplications(
        club.club.clubAddress
      );

      applicationsForClub.forEach((application) => {
        const date = application.createdAt;
        applicationData.push({
          clubImage: club.club.clubImageUrl,
          clubName: club.club.clubName,
          date: dayjs(date).format("DD/MM/YY HH:mm"),
          clubAddress: application.clubAddress,
        });
      });
    }
    return applicationData;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const withdrawTokensFromDashboard = async (
  withdrawal: IWithdrawalDataDashboard,
  wallet: AnchorWallet,
  withdrawals: IWithdrawalDataDashboard[]
) => {
  try {
    await claimWithdrawalAmount(
      wallet,
      withdrawal.withdrawal,
      withdrawal.withdrawalDataAddress,
      withdrawal.club.clubName
    );
    const addedWithdrawals = [...withdrawals!];
    const existingWithdrawalIndex = addedWithdrawals.findIndex(
      (addedWithdrawal) =>
        addedWithdrawal.withdrawalDataAddress.toString() ===
        withdrawal.withdrawalDataAddress.toString()
    );
    if (existingWithdrawalIndex < 0) {
      throw new Error();
    }
    let existingWithdrawal = addedWithdrawals[existingWithdrawalIndex];
    existingWithdrawal = { ...existingWithdrawal, hasWithdrawn: true };
    addedWithdrawals[existingWithdrawalIndex] = existingWithdrawal;
    return addedWithdrawals;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export async function getAllStakingAccountsForDashboardClubs(
  wallet: AnchorWallet
) {
  try {
    const stakeConfigs: PublicKey[] = [];
    const stakeRecords: PublicKey[] = [];
    const clubProgram = programFactory();
    const stakeConfigsWithClubInfo: {
      stakeConfig: PublicKey;
      clubInfo: IDashboardBasicClubData;
    }[] = [];

    const allClubs: any = await clubProgram.account.clubData.all();
    const allClubsFromApi = await getDashboardClubs();

    for (const memberClub of await allClubs) {
      const club: IBasicClubData =
        allClubsFromApi[memberClub.publicKey.toString()];
      if (club) {
        const [realmAddress] = await PublicKey.findProgramAddress(
          [governanceSeed, Buffer.from(memberClub.account.name)],
          splGovernanceProgram
        );
        const [clubData] = await PublicKey.findProgramAddress(
          [unqClubSeed, realmAddress.toBuffer()],
          clubProgram.programId
        );

        for (let i = 0; i < memberClub.account.stakeCount; i++) {
          const stakeCount = Buffer.alloc(4);
          stakeCount.writeInt32LE(i + 1, 0);
          const [stakeConfigAddress] = await PublicKey.findProgramAddress(
            [unqClubSeed, clubData.toBuffer(), stakeConfigSeed, stakeCount],
            clubProgram.programId
          );
          stakeConfigsWithClubInfo.push({
            clubInfo: {
              clubAddress: memberClub.publicKey,
              clubImageUrl: memberClub.account.clubImageUrl,
              clubName: memberClub.account.name,
              realmAddress: memberClub.account.realm,
              communityMint: memberClub.account.communityMint,
              numberOfMembers: memberClub.account.memberCount,
              roleConfig: memberClub.account.roleConfig,
              stakeCount: memberClub.account.stakeCount,
              type: memberClub.account.type,
            },
            stakeConfig: stakeConfigAddress,
          });
          const [stakeRecord] = await PublicKey.findProgramAddress(
            [
              unqClubSeed,
              wallet.publicKey.toBuffer(),
              stakeRecordSeed,
              stakeConfigAddress.toBuffer(),
            ],
            clubProgram.programId
          );

          stakeConfigs.push(stakeConfigAddress);
          stakeRecords.push(stakeRecord);
        }
      }
    }

    const stakeConfigAccounts: any[] =
      await clubProgram.account.stakeConfig.fetchMultiple(stakeConfigs);
    const stakeRecordsAccounts: any[] =
      await clubProgram.account.stakeRecord.all([
        {
          memcmp: {
            offset: 8,
            bytes: wallet.publicKey.toBase58(),
          },
        },
      ]);

    const stakeConfigsInfo: IDashboardStakingInfo[] = [];
    for (const [index, stakeConfigInfo] of stakeConfigAccounts.entries()) {
      const nftMetadata =
        stakeConfigInfo.nftStakeRecord &&
        (await getMintMetadata(stakeConfigInfo.nftStakeRecord.tokenMint));
      const stakingAccount = {
        accountAddress: stakeConfigs[index],
        stakingRewardPercentage: getFixedRewardPercentage(
          Object.keys(
            stakeConfigInfo.stakePeriodType
          )[0] as StakePeriodTypeSolana
        ),
        stakingPeriodType: getStakingPeriodType(
          Object.keys(
            stakeConfigInfo.stakePeriodType
          )[0] as StakePeriodTypeSolana
        ),
        capAmount: stakeConfigInfo.capAmount.toNumber() / UNQ_PER_AMOUNT,
        name: stakeConfigInfo.name,
        raisedAmount: stakeConfigInfo.raisedAmount.toNumber() / UNQ_PER_AMOUNT,
        status: mapStakingStatusFromSolana(
          Object.keys(stakeConfigInfo.status)[0] as StakingStatusSolana
        ),
        stakingIndex: stakeConfigInfo.stakeIndex,
        stakePeriodEnd: stakeConfigInfo.stakePeriodEnd
          ? (stakeConfigInfo.stakePeriodEnd as number)
          : undefined,
        nftStakeRecord: stakeConfigInfo.nftStakeRecord && {
          additionalReward: getAdditionalReward(
            Object.keys(stakeConfigInfo.nftStakeRecord.rarity)[0] as Rarity
          ),
          hasClaimed: stakeConfigInfo.nftStakeRecord.hasClaimed,
          membersTokenAccount: stakeConfigInfo.nftStakeRecord.tokenAccount,
          nftOwner: stakeConfigInfo.nftStakeRecord.nftOwner,
          stakedNftTokenAccount: stakeConfigInfo.nftStakeRecord.stakedNft,
          unstakePeriodEnd: stakeConfigInfo.nftStakeRecord.unstakePeriodEnd,
          mint: stakeConfigInfo.nftStakeRecord.tokenMint,
          rarity: Object.keys(
            stakeConfigInfo.nftStakeRecord.rarity
          )[0] as Rarity,
          nftImgUrl: nftMetadata.image,
          nftName: nftMetadata.name,
        },
      };

      //TODO@milica: check if this returns correct object
      const memberRecordAccount = stakeRecordsAccounts.find(
        (sr) =>
          sr.account.stakeConfig.toString() === stakeConfigs[index].toString()
      );

      let memberStakingInfo: IMemberStakingInfo | undefined = undefined;

      if (memberRecordAccount) {
        memberStakingInfo = {
          //TODO@milica-staking: check this
          address: stakeRecords[index],
          claimAmount:
            memberRecordAccount.account.claimAmount.toNumber() / UNQ_PER_AMOUNT,

          depositAmount:
            memberRecordAccount.account.amount.toNumber() / UNQ_PER_AMOUNT,
          hasClaimed: memberRecordAccount.account.hasClaimed,
          hasUnstaked: memberRecordAccount.account.hasUnstaked,
          stakeOwner: wallet.publicKey,
          unstakePeriodEnd: memberRecordAccount.account.unstakePeriodEnd
            ? (memberRecordAccount.account.unstakePeriodEnd as number)
            : undefined,
        };
      }

      const stakingStatus = getStatusForStakingAccount(
        memberStakingInfo,
        stakingAccount
      );

      if (
        stakeRecordsAccounts.find(
          (item) =>
            item.account.stakeConfig.toString() ===
            stakeConfigs[index].toString()
        ) ||
        stakingAccount?.nftStakeRecord?.nftOwner.toString() ===
          wallet.publicKey.toString()
      ) {
        stakeConfigsInfo.push({
          stakingAccountInfo: stakingAccount,
          memberStakingInfo,
          stakingStatus,
          club: stakeConfigsWithClubInfo.find(
            (item) =>
              item.stakeConfig.toString() ===
              stakingAccount.accountAddress.toString()
          )!.clubInfo,
        });
      }
    }

    return stakeConfigsInfo;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const getTotalStakedAmount = (stakings) => {
  let totakStaked = 0;
  stakings!.forEach((item) => {
    if (item.memberStakingInfo) {
      totakStaked = totakStaked + item.memberStakingInfo.depositAmount;
    }
  });
  return totakStaked;
};

export async function getUsersApplications(
  clubs: IMembersClubData[],
  wallet: AnchorWallet
) {
  try {
    const applicationsData: IDashboardApplication[] = [];
    const applications = await getUserApplications(wallet.publicKey.toString());
    if (!applications) return [];
    for (const application of applications) {
      applicationsData.push({
        date: dayjs(application.application.createdAt).format(
          "DD/MM/YYYY HH:mm"
        ),
        clubAddress: application.application.clubAddress,
        clubImage: application.clubImage,
        clubName: application.clubName,
      });
    }
    return applicationsData;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function claimMultipleAmounts(
  withdrawals: IWithdrawalDataDashboard[],
  wallet: AnchorWallet
) {
  try {
    const escrowProgram = escrowProgramFactory();
    const clubProgram = programFactory();
    const instructionsSet: IInstructionSet[] = [];
    for (const withdrawal of withdrawals) {
      const instructions: TransactionInstruction[] = [];
      const countBuff = Buffer.alloc(4);
      countBuff.writeInt32LE(withdrawal.fundraiseCount, 0);
      const [withdrawalRecord] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          withdrawal.withdrawal.toBuffer(),
          withdrawalRecordSeed,
          wallet.publicKey.toBuffer(),
        ],
        escrowProgram.programId
      );

      const [memberPDA] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          new PublicKey(withdrawal.club.clubAddress).toBuffer(),
          unqClubMemberSeed,
          wallet.publicKey.toBuffer(),
        ],
        clubProgram.programId
      );

      let membersTA: PublicKey;

      const membersTAs = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
        wallet.publicKey,
        {
          mint: withdrawal.withdrawalMint,
        }
      );

      if (membersTAs.value.length > 0) {
        membersTA = membersTAs.value[0].pubkey;
      } else {
        membersTA = await Token.getAssociatedTokenAddress(
          ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
          TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
          withdrawal.withdrawalMint, // mint
          wallet.publicKey, // owner
          false
        );
        instructions.push(
          Token.createAssociatedTokenAccountInstruction(
            ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
            TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
            withdrawal.withdrawalMint, // mint
            membersTA, // ata
            wallet.publicKey, // owner of token account
            wallet.publicKey // fee payer
          )
        );
      }

      const { treasuryData } = await getFinancialOfferPDAs(
        withdrawal.club.clubName,
        wallet
      );
      const [financialRecord] = await getFinancialRecordPDA(
        treasuryData,
        wallet.publicKey
      );

      const requestComputeUnitsTx = ComputeBudgetProgram.requestUnits({
        additionalFee: 0,
        units: 300000,
      });
      instructions.push(requestComputeUnitsTx);

      const claimIx = escrowProgram.instruction.withdraw({
        accounts: {
          payer: wallet.publicKey,
          withdrawalRecord: withdrawalRecord,
          withdrawal: withdrawal.withdrawal,
          withdrawalData: withdrawal.withdrawalDataAddress,
          memberData: memberPDA,
          financialRecord: financialRecord,
          systemProgram: SystemProgram.programId,
          memberTokens: membersTA,
          tokenProgram: TOKEN_PROGRAM_ID,
          treasuryData,
        },
      });
      instructions.push(claimIx);
      instructionsSet.push({
        description: `Claiming ${withdrawal.withdrawalRecordAmount} ${
          withdrawal.withdrawalMintSymbol ?? "SOL"
        }`,
        instructions: [...instructions],
      });
    }
    await sendTransactions(RPC_CONNECTION, wallet, instructionsSet);
    const updatedWithdrawals = withdrawals.map((withdrawal) => {
      return { ...withdrawal, hasWithdrawn: true };
    });
    return updatedWithdrawals;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const getTokensForAllTreasuries = async (
  treasuryData: (IDashboardTreasury | null)[]
): Promise<IDashboardTokenAccountInfo[]> => {
  try {
    const loadedTreasuryTokens: IDashboardTokenAccountInfo[] = [];
    for (const treasury of treasuryData) {
      if (treasury) {
        const treasuryTokenAccounts = await getTreasuyTokenAccountsInfo(
          treasury.treasuryAddress,
          treasury.treasuryLamports
        );
        loadedTreasuryTokens.push({
          tokenAccounts: treasuryTokenAccounts,
          treasuryAddress: treasury.treasuryAddress,
        });
      }
    }
    return loadedTreasuryTokens;
  } catch (error) {
    throw error;
  }
};

export const getOwnershipInClub = (depositRecordData: any) => {
  try {
    let boughtAmount = 0;
    let soldAmount = 0;

    const deposits: IDepositRecord[] = [];
    for (const depositRecord of depositRecordData.depositRecords) {
      if (depositRecord.tradedRight) {
        boughtAmount += depositRecord.tradedRight
          .filter(
            (tr) =>
              Object.keys(tr.tradeType)[0] === TradedRightType.Buy ||
              Object.keys(tr.tradeType)[0] === TradedRightType.BuyReserved
          )
          .reduce((acc, trade) => {
            return acc + trade.amount.toNumber();
          }, 0);
        soldAmount += depositRecord.tradedRight
          .filter(
            (tr) =>
              Object.keys(tr.tradeType)[0] === TradedRightType.Sell ||
              Object.keys(tr.tradeType)[0] === TradedRightType.SellReserved
          )
          .reduce((acc, trade) => {
            return acc + trade.amount.toNumber();
          }, 0);
      }
      deposits.push(mapDepositRecords(depositRecord));
    }

    return {
      accumulatedAmount:
        depositRecordData.depositRecords.length > 0
          ? depositRecordData.depositRecords[
              depositRecordData.depositRecords.length - 1
            ].accumulatedAmount.toNumber()
          : 0,
      boughtAmount,
      soldAmount,
      deposits,
    };
  } catch (error) {
    throw error;
  }
};
